/**
 * @author Simeon Artamonov <simeon@cross-works.co.uk>
 * @company Cross Works Ltd
 */
import { ref, computed, onMounted, onBeforeUnmount } from 'vue'

// Global reactive state shared across all instances
const mouseX = ref<number | null>(null)
const windowWidth = ref(window.innerWidth)
const isMobile = ref(false)
const manualLeftPreview = ref(false)
const manualRightPreview = ref(false)

// Global reactive state shared across all instances

// DOM measurement cache for performance
const windowCache = {
  width: window.innerWidth,
  lastUpdate: 0,
  isValid: false
}

// Cache duration for window measurements (200ms)
const WINDOW_CACHE_DURATION = 200

export function useMousePreview() {

  // Cached window width update
  const updateWindowCache = () => {
    const now = Date.now()
    
    // Only update cache if it's stale
    if (windowCache.isValid && (now - windowCache.lastUpdate) < WINDOW_CACHE_DURATION) {
      return windowCache.width
    }
    
    windowCache.width = window.innerWidth
    windowCache.lastUpdate = now
    windowCache.isValid = true
    
    return windowCache.width
  }

  const checkMobile = () => {
    // Use cached measurement to reduce DOM queries
    const width = updateWindowCache()
    
    // Align with CSS breakpoint where panels are hidden (max-width: 991px)
    isMobile.value = width <= 991
    windowWidth.value = width
  }

  const getScreenSize = () => {
    // Use cached width instead of direct window.innerWidth
    const width = updateWindowCache()
    
    // Only categorize desktop/laptop sizes since mobile is handled separately
    if (width <= 991) return 'mobile' // Panels hidden anyway
    if (width < 1200) return 'small-desktop'   // Small laptops/desktops
    if (width < 1640) return 'medium-desktop'  // Medium desktops
    return 'large-desktop'  // Large desktops
  }

  const throttleRAF = (fn: (...args: any[]) => void) => {
    let id = 0
    return (...args: any[]) => {
      if (id) cancelAnimationFrame(id)
      id = requestAnimationFrame(() => fn(...args))
    }
  }

  const handleMouseMove = throttleRAF((e: MouseEvent) => {
    mouseX.value = e.clientX
  })

  // Just add this to your existing handleMouseLeave function:

const handleMouseLeave = () => {
  // Simply reset the state - let CSS transitions handle the animation
  mouseX.value = null
  manualLeftPreview.value = false
  manualRightPreview.value = false
}

  const handleResize = () => {
    // Invalidate cache on resize
    windowCache.isValid = false
    checkMobile()
  }

  // Calculate edge-based zones that protect main content on all desktop/laptop sizes
  const getPreviewZones = () => {
    const screenSize = getScreenSize()
    const width = windowWidth.value
    
    // On mobile, panels are hidden so no zones needed
    if (screenSize === 'mobile') {
      return { leftZoneEnd: 0, rightZoneStart: width }
    }
    
    // For desktop/laptop sizes, create very conservative edge zones
    // that only activate at the very edges to protect main content
    let leftZoneEnd: number
    let rightZoneStart: number
    
    switch (screenSize) {
      case 'small-desktop':
        // Small laptops need very tight edge-only zones to preserve content area
        leftZoneEnd = width * 0.08    // 8% from left edge
        rightZoneStart = width * 0.92  // 8% from right edge (84% safe center)
        break
      case 'medium-desktop':
        // Medium desktops can have slightly larger but still conservative zones
        leftZoneEnd = width * 0.10    // 10% from left edge  
        rightZoneStart = width * 0.90  // 10% from right edge (80% safe center)
        break
      default: // large-desktop
        // Large desktops get moderate zones but still prioritize content safety
        leftZoneEnd = width * 0.12    // 12% from left edge
        rightZoneStart = width * 0.88  // 12% from right edge (76% safe center)
        break
    }
    
    return { leftZoneEnd, rightZoneStart }
  }

  // Calculate dynamic panel position based on mouse position within the zone
  const getDynamicPanelPosition = (side: 'left' | 'right') => {
    if (isMobile.value || mouseX.value === null) return null
    
    const { leftZoneEnd, rightZoneStart } = getPreviewZones()
    const width = windowWidth.value
    
    if (side === 'left' && mouseX.value <= leftZoneEnd) {
  
      const tabOffsetPercent = (40 / width) * 100  // 40px = half of 80px tab width
      
      const mousePercent = (mouseX.value / width) * 100
      const translateX = mousePercent - 100 - tabOffsetPercent
      
      // Clamp to reasonable bounds (show between 5% and 40% of panel)
      return Math.max(-95, Math.min(-60, translateX))
    }
    
    if (side === 'right' && mouseX.value >= rightZoneStart) {

      const tabOffsetPercent = (40 / width) * 100  // 40px = half of 80px tab width
      
      const mousePercent = (mouseX.value / width) * 100
      const translateX = mousePercent + tabOffsetPercent
      
      // Clamp to reasonable bounds (show between 5% and 40% of panel)
      return Math.min(95, Math.max(60, translateX))
    }
    
    return null
  }

  // Get minimal translate position for emit-based preview
  const getMinimalPanelPosition = (side: 'left' | 'right') => {
    if (isMobile.value) return null
    
    // Return fixed minimal translate positions
    if (side === 'left') {
      return -92  // Show just a bit of the left panel
    } else {
      return 92   // Show just a bit of the right panel
    }
  }

  // Emit-based control functions
  const showLeftPreview = () => {
    if (!isMobile.value && !isPanelActiveOrDragging()) {
      manualLeftPreview.value = true
    }
  }

  const hideLeftPreview = () => {
    manualLeftPreview.value = false
  }

  const showRightPreview = () => {
    if (!isMobile.value && !isPanelActiveOrDragging()) {
      manualRightPreview.value = true
    }
  }

  const hideRightPreview = () => {
    manualRightPreview.value = false
  }

  // Check if panels are open or being dragged
  const isPanelActiveOrDragging = () => {
    return document.body.classList.contains('panel-open') || 
           document.body.classList.contains('panel-near-open')
  }

  // Mouse-based preview detection
  const mouseBasedLeftPreview = computed(() => {
    if (isMobile.value || mouseX.value === null) return false
    
    if (isPanelActiveOrDragging()) return false
    
    const { leftZoneEnd } = getPreviewZones()
    return mouseX.value <= leftZoneEnd
  })

  const mouseBasedRightPreview = computed(() => {
    if (isMobile.value || mouseX.value === null) return false
    
    // Disable mouse preview when panels are open or being dragged
    if (isPanelActiveOrDragging()) return false
    
    const { rightZoneStart } = getPreviewZones()
    return mouseX.value >= rightZoneStart
  })

  // Combined preview states (manual control takes priority)
  const showLeftPreviewComputed = computed(() => {
    // Disable all previews when panels are open or being dragged
    if (isPanelActiveOrDragging()) return false
    
    return manualLeftPreview.value || mouseBasedLeftPreview.value
  })

  const showRightPreviewComputed = computed(() => {
    // Disable all previews when panels are open or being dragged
    if (isPanelActiveOrDragging()) return false
    
    return manualRightPreview.value || mouseBasedRightPreview.value
  })

  // Combined panel positions (use minimal for manual, dynamic for mouse)
  const leftPanelPosition = computed(() => {
    if (manualLeftPreview.value) {
      return getMinimalPanelPosition('left')
    }
    return getDynamicPanelPosition('left')
  })

  const rightPanelPosition = computed(() => {
    if (manualRightPreview.value) {
      return getMinimalPanelPosition('right')
    }
    return getDynamicPanelPosition('right')
  })

  // Function to initialize listeners
  const initializeListeners = () => {
    document.addEventListener('mousemove', handleMouseMove)
    document.addEventListener('mouseleave', handleMouseLeave)
    window.addEventListener('resize', handleResize, { passive: true })
    checkMobile()
  }

  // Function to cleanup listeners
  const cleanupListeners = () => {
    // document.removeEventListener('mousemove', handleMouseMove)
    // document.removeEventListener('mouseleave', handleMouseLeave)
    // window.removeEventListener('resize', handleResize)
  }

  onMounted(() => {
    // Initialize listeners when component mounts
    initializeListeners()
  })

  onBeforeUnmount(() => {
    // Clean up listeners when component unmounts
    cleanupListeners()
  })

  return {
    // Existing computed properties (now combined)
    showLeftPreview: showLeftPreviewComputed,
    showRightPreview: showRightPreviewComputed,
    leftPanelPosition,
    rightPanelPosition,
    isMobile,
    
    // New emit-based control functions
    showLeft: showLeftPreview,
    hideLeft: hideLeftPreview,
    showRight: showRightPreview,
    hideRight: hideRightPreview,
    
    // Manual listener control
    initializeListeners,
    cleanupListeners
  }
} 